import { useDispatch, useSelector } from "react-redux";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import Pagination from "../../Services/Pagination";
import moment from "moment";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";
import useConfirm from "../../Services/Confirmation";
import { numberFormatCustom } from "../../Services/DateFunc";

const canCreateCollection = (userData) => (
    userData.role_slug === "super_admin"
    || userData.caps_list.includes("add_collection")
    || userData.caps_list.includes("manage_collections")
);

const canEditCollection = (userData) => (
    userData.role_slug === "super_admin"
    || userData.caps_list.includes("edit_collection")
    || userData.caps_list.includes("manage_collections")
);

const CollectionsList = () => {
    const dispatch = useDispatch();
    const customConfirm = useConfirm();
    const { userData, accessToken } = useSelector((state) => state.user);
    const [showFilter, setShowFilter] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationData, setPaginationData] = useState({ total: 0, per_page: 20 });
    const [filter, setFilter] = useState({
        org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id,
        div_id: userData.role_slug === "super_admin" || userData.role_slug === "org_admin" ? 0 : userData.user_div,
        status: "",
        search: "",
    });
    const [orgList, setOrgList] = useState([]);
    const [divList, setDivList] = useState([]);
    const [collectionsList, setCollectionsList] = useState([]);

    const filterHandler = (key, value) => {
        setFilter({ ...filter, [key]: value });
    };

    useEffect(() => {
        dispatch(setPageTitle("Collections"));
        dispatch(setMainLoading(true));
        const promiseArray = [];
        if (userData.role_slug === "super_admin") {
            promiseArray.push(PostAxios(`masters/organization`, { page: 1, perPage: 1000 }, accessToken));
        }
        if (userData.role_slug === "super_admin" || userData.role_slug === "org_admin") {
            promiseArray.push(PostAxios(`masters/division-list`, {
                org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id,
                page: 1,
                perPage: 1000,
            }, accessToken));
        }
        if (promiseArray.length > 0) {
            Promise.all(promiseArray)
                .then((response) => {
                    if (response[0]?.config?.url === "masters/organization" && response[0].data.status === "1") {
                        setOrgList(response[0].data.data);
                    } else if (response[0]?.data?.status === "1") {
                        setDivList(response[0].data.data);
                    }
                    if (response[1]?.config?.url === "masters/division-list" && response[1].data.status === "1") {
                        setDivList(response[1].data.data);
                    }
                    runFilter();
                })
                .catch(() => runFilter());
        } else {
            runFilter();
        }
    }, [dispatch, accessToken, userData]);

    const runFilter = (pageNo = 0) => {
        dispatch(setMainLoading(true));
        setCollectionsList([]);
        const postData = { ...filter };
        if (pageNo > 0) {
            postData.page = pageNo;
            postData.perPage = paginationData.per_page;
        }
        PostAxios(`collection/collection-list`, postData, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    if (data?.totalRecords !== undefined) {
                        setPaginationData({ total: data.totalRecords, per_page: data.perPage });
                    }
                    setCollectionsList(data.list || []);
                } else {
                    dispatch(setToastData({
                        message: (data.errors && Object.values(data.errors).length > 0)
                            ? ConvertErrorArrayToString(data.errors)
                            : data.message,
                        type: "error",
                        showCloseBtn: true,
                    }));
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    const filteredDivList = useMemo(() => {
        const tempL = [];
        const orgId = parseInt(filter.org_id, 10);
        for (const div of divList) {
            if (parseInt(div.org_id, 10) === orgId || orgId === 0) {
                tempL.push(div);
            }
        }
        return tempL;
    }, [filter.org_id, divList]);

    const deleteCollection = async (collectionId) => {
        const userChoice = await customConfirm({
            description: "Do you want to delete this collection?",
        });
        if (!userChoice) return;

        dispatch(setMainLoading(true));
        PostAxios(`collection/delete-collection`, { collection_id: collectionId }, accessToken)
            .then(({ data }) => {
                if (data.status === "1") {
                    dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                    runFilter(currentPage);
                } else {
                    dispatch(setToastData({
                        message: (data.errors && Object.values(data.errors).length > 0)
                            ? ConvertErrorArrayToString(data.errors)
                            : data.message,
                        type: "error",
                        showCloseBtn: true,
                    }));
                }
            })
            .catch((err) => responseCatchFunc(dispatch, err))
            .finally(() => dispatch(setMainLoading(false)));
    };

    return (
        <div className="main-form-sec">
            {canCreateCollection(userData) && (
                <div className="top-right-btn p-0">
                    <Link to="/collections/add" className="btn-blue btn br-0">Add Collection</Link>
                </div>
            )}
            <div className="top-reports-filter">
                <div className="right-filter-sec">
                    <button className="filter-toggle" type="button" onClick={() => setShowFilter(!showFilter)}>
                        <i className="fa-solid fa-filter" />
                    </button>
                    <div className={`p-0 filter-search form-field-group report-filter ${showFilter ? "" : "filter-active"}`}>
                        {userData.role_slug === "super_admin" && (
                            <div className="report-filter-field">
                                <label className="select-input-outer">Organization</label>
                                <div className="static-items-outer">
                                    <select
                                        className="select-input"
                                        value={filter.org_id}
                                        onChange={(e) => setFilter({ ...filter, org_id: e.target.value, div_id: 0 })}
                                    >
                                        <option value="0">All</option>
                                        {orgList.map((item) => (
                                            <option key={`org-${item.ID}`} value={item.ID}>{item.short_name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                        {(userData.role_slug === "super_admin" || userData.role_slug === "org_admin") && (
                            <div className="report-filter-field">
                                <label className="select-input-outer">Division</label>
                                <div className="static-items-outer">
                                    <select
                                        className="select-input"
                                        value={filter.div_id}
                                        onChange={(e) => filterHandler("div_id", e.target.value)}
                                    >
                                        <option value="0">All</option>
                                        {filteredDivList.map((item) => (
                                            <option key={`div-${item.ID}`} value={item.ID}>
                                                {item.division_name} ({item.division_number})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                        <div className="report-filter-field">
                            <label className="select-input-outer">Status</label>
                            <div className="static-items-outer">
                                <select
                                    className="select-input"
                                    value={filter.status}
                                    onChange={(e) => filterHandler("status", e.target.value)}
                                >
                                    <option value="">All</option>
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="report-filter-field">
                            <label className="select-input-outer">Search</label>
                            <div className="static-items-outer">
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Collection name..."
                                    value={filter.search}
                                    onChange={(e) => filterHandler("search", e.target.value)}
                                />
                            </div>
                        </div>
                        <button type="button" className="btn btn-blue ms-2" onClick={() => { setCurrentPage(1); runFilter(1); }}>
                            Submit
                        </button>
                    </div>
                </div>
            </div>

            <div className="table-main">
                <table className="table">
                    <thead>
                        <tr>
                            <th>S.No.</th>
                            {userData.role_slug === "super_admin" && <th>Organization</th>}
                            <th>Collection Name</th>
                            <th>Divisions</th>
                            <th>Target</th>
                            <th>Collected</th>
                            <th>End Date</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {collectionsList.length === 0 && (
                            <tr>
                                <td colSpan={userData.role_slug === "super_admin" ? 10 : 9} className="text-center text-muted py-4">
                                    No collections found.
                                </td>
                            </tr>
                        )}
                        {collectionsList.map((item, index) => (
                            <tr key={`collection-${item.id}`}>
                                <td>{(currentPage - 1) * paginationData.per_page + index + 1}</td>
                                {userData.role_slug === "super_admin" && <td>{item.org_name}</td>}
                                <td>{item.collection_name}</td>
                                <td>{item.division_names || "-"}</td>
                                <td>{numberFormatCustom(parseFloat(item.target_amount || 0))}</td>
                                <td>{numberFormatCustom(parseFloat(item.total_collected || 0))}</td>
                                <td>{moment(item.end_date).format("DD MMM YYYY")}</td>
                                <td>{item.collection_type} / {item.collection_frequency}</td>
                                <td>
                                    <span className={`status-text ${parseInt(item.status, 10) === 1 ? "approved" : "rejected"}`}>
                                        {parseInt(item.status, 10) === 1 ? "Active" : "Inactive"}
                                    </span>
                                </td>
                                <td>
                                    <Link to="/collections/view" state={{ collection_id: item.id }} className="btn btn-sm btn-blue me-1">
                                        View
                                    </Link>
                                    {canEditCollection(userData) && (
                                        <>
                                            <Link to="/collections/edit" state={{ collection_id: item.id }} className="btn btn-sm btn-blue me-1">
                                                Edit
                                            </Link>
                                            <button type="button" className="btn btn-sm status-btn rejected" style={{ float: "none" }} onClick={() => deleteCollection(item.id)}>
                                                Delete
                                            </button>
                                        </>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalCount={paginationData.total}
                pageSize={paginationData.per_page}
                onPageChange={(page) => {
                    setCurrentPage(page);
                    runFilter(page);
                }}
            />
        </div>
    );
};

export default CollectionsList;
